import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// WebSocket connection
let ws: WebSocket | null = null;
const priceUpdateHandlers: Record<string, ((data: any) => void)[]> = {};

// Connect to WebSocket for live price updates
export const connectWebSocket = (clinicId: string) => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    console.log('WebSocket already connected');
    return;
  }
  
  // Create WebSocket connection
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${window.location.host}/ws`;
  
  console.log(`Connecting to WebSocket at ${wsUrl}`);
  ws = new WebSocket(wsUrl);
  
  ws.onopen = () => {
    console.log('WebSocket connected');
    // Register for updates for this clinic
    if (ws) {
      ws.send(JSON.stringify({
        type: 'register',
        clinicId
      }));
    }
  };
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      console.log('WebSocket message received:', data);
      
      if (data.type === 'price_update' && data.appointment_type_id) {
        // Notify all registered handlers for this appointment type
        const typeId = data.appointment_type_id;
        if (priceUpdateHandlers[typeId]) {
          priceUpdateHandlers[typeId].forEach(handler => handler(data));
        }
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  };
  
  ws.onerror = (error) => {
    console.error('WebSocket error:', error);
  };
  
  ws.onclose = () => {
    console.log('WebSocket disconnected');
    ws = null;
  };
};

// Register handler for price updates for specific appointment type
export const registerPriceUpdateHandler = (
  appointmentTypeId: string, 
  handler: (data: any) => void
): () => void => {
  if (!priceUpdateHandlers[appointmentTypeId]) {
    priceUpdateHandlers[appointmentTypeId] = [];
  }
  
  priceUpdateHandlers[appointmentTypeId].push(handler);
  
  // Return unregister function
  return () => {
    if (priceUpdateHandlers[appointmentTypeId]) {
      priceUpdateHandlers[appointmentTypeId] = priceUpdateHandlers[appointmentTypeId]
        .filter(h => h !== handler);
    }
  };
};

// API for fetching clinic information
export const getClinicInfo = async (clinicId?: string, clinicPath?: string) => {
  try {
    const params = clinicId ? { id: clinicId } : { path: clinicPath };
    const response = await axios.get(`${API_BASE_URL}/api/clinics`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching clinic info:', error);
    throw error;
  }
};

// API for fetching services (appointment types) for a clinic
export const getClinicServices = async (clinicId: number) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/clinics/${clinicId}/appointment-types`);
    return response.data;
  } catch (error) {
    console.error('Error fetching clinic services:', error);
    throw error;
  }
};

// API for fetching available times
export const getAvailableTimes = async (params: {
  clinic_id: string;
  appointment_type_id: string;
  practitioner_id?: string;
  from_date?: string;
  to_date?: string;
}) => {
  try {
    // Set default from_date to today if not provided
    const from_date = params.from_date || new Date().toISOString().split('T')[0];
    
    // Set default to_date to 3 days from from_date if not provided
    // (Cliniko API has strict limitations for available_times endpoint)
    let to_date = params.to_date;
    if (!to_date) {
      const fromDate = new Date(from_date);
      const threeDaysLater = new Date(fromDate);
      threeDaysLater.setDate(fromDate.getDate() + 2); // 3 days total (inclusive)
      
      // Format dates explicitly in YYYY-MM-DD format
      const padZero = (num) => num < 10 ? `0${num}` : `${num}`;
      to_date = `${threeDaysLater.getFullYear()}-${padZero(threeDaysLater.getMonth() + 1)}-${padZero(threeDaysLater.getDate())}`;
    }
    
    // The server expects appointment_type (not appointment_type_id)
    // This is because Cliniko's API uses these parameter names
    const apiParams: any = {
      appointment_type: params.appointment_type_id,
      from: from_date, // Cliniko expects 'from', not 'from_date'
      to: to_date,     // Cliniko expects 'to', not 'to_date'
      clinic_id: params.clinic_id,
    };
    
    // Add practitioner_id if provided
    if (params.practitioner_id) {
      apiParams.practitioner_id = params.practitioner_id;
    }
    
    console.log('Fetching available times with params:', apiParams);
    const response = await axios.get(`${API_BASE_URL}/api/available-times`, { 
      params: apiParams,
      timeout: 30000 // Add 30 second timeout to prevent hanging
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available times:', error);
    throw error;
  }
};

// Fetch practitioners
export const getPractitioners = async (clinicId: number) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/practitioners`, {
      params: { clinic_id: clinicId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching practitioners:', error);
    throw error;
  }
};

// API for creating a booking
export const createBooking = async (bookingData: {
  clinic_id: string;
  appointment_type_id: string;
  practitioner_id: string;
  starts_at: string;
  ends_at: string;
  patient_first_name: string;
  patient_last_name: string;
  patient_email: string;
  patient_phone?: string;
  notes?: string;
}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/appointments`, bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

// API for initiating payment
export const initiatePayment = async (paymentData: {
  clinic_id: string;
  appointment_type_id: string;
  practitioner_id?: string;
  amount: number;
  patient_email: string;
  patient_name: string;
  // Additional appointment details
  starts_at?: string;
  ends_at?: string;
  patient_first_name?: string;
  patient_last_name?: string;
  patient_phone?: string;
  notes?: string;
  returnUrl?: string;
}) => {
  try {
    // Get existing returnUrl from localStorage or search params if not provided
    if (!paymentData.returnUrl) {
      // First check localStorage
      const storedReturnUrl = localStorage.getItem('bookingReturnUrl');
      if (storedReturnUrl) {
        paymentData.returnUrl = storedReturnUrl;
        console.log('Using stored return URL for payment:', storedReturnUrl);
      } else {
        // Then check URL search params
        const searchParams = new URLSearchParams(window.location.search);
        const urlReturnUrl = searchParams.get('returnUrl');
        if (urlReturnUrl) {
          paymentData.returnUrl = urlReturnUrl;
          console.log('Using URL parameter return URL for payment:', urlReturnUrl);
        }
      }
    }

    console.log('Initiating payment with data:', {
      ...paymentData,
      hasReturnUrl: !!paymentData.returnUrl
    });
    
    const response = await axios.post(`${API_BASE_URL}/api/payments/initiate`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error initiating payment:', error);
    throw error;
  }
};

// API for checking payment status
export const checkPaymentStatus = async (accessCode: string) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/payments/status/${accessCode}`);
    return response.data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};

// Utility function to format a date for display
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

// Utility function to format a time for display
export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Utility function to format price
export const formatPrice = (price: number | null): string => {
  if (price === null) return 'Price on consultation';
  if (typeof price !== 'number') return `$${price}`; 
  return `$${price.toFixed(2)}`;
};